import {css, Typography, useTheme} from '@mui/material';
import {
  ConfigObjectType,
  ConfirmDialog,
  DlMuiDynamicIcon,
  ExportUtils,
  GetStylesFunction,
  useApiQuery,
  useCurrentLocale,
} from '@wallaby-ui/core';
import {useFormatters, WallabyCoreSchema} from '@wallaby-ui/document';
import {useSnackbar} from 'notistack';
import {useCallback} from 'react';
import * as React from 'react';

import {biochemistryParams} from './hospitalizedPatientsBiochemistry';
import {PatientFiltersData} from './HospitalizedPatientsLayout';
import {microbiologyParams} from './hospitalizedPatientsMicrobiology';
import {otherParams} from './hospitalizedPatientsOther';

export function useHospitalizedPatientsExport(filters: PatientFiltersData, config: ConfigObjectType, totalCount: number | undefined) {
  const currentLocale = useCurrentLocale();
  const theme = useTheme();
  const muiStyles = getStyles({theme});
  const {t} = useFormatters();
  const snackBar = useSnackbar();

  const apiUrl = config.service.url.maxExportRows;

  const {data: maxExportedRows} = useApiQuery<number | undefined>([apiUrl], null, {
    axiosConfig: {
      url: apiUrl,
    },
    reactQueryOptions: {
      enabled: true,
    },
  });

  const downloadXlsxExport = useCallback(() => {
    return ExportUtils.handleExportXlsx({
      url: config.service.url.hospitalizationsExport,
      filters: {
        patientFullName: filters.patientFullName,
        patientIdentificationNumber: filters.patientIdentificationNumber,
        patientSrcId: filters.patientSrcId,
        lastEncounterSubOrganizationIds: filters.lastEncounterSubOrganizationIds,
        encounterOrganizationIds: filters.encounterOrganizationIds,
        atcHierarchyIds1: filters.atcHierarchyIds1,
        atcHierarchyIds2: filters.atcHierarchyIds2,
        atcHierarchyIds3: filters.atcHierarchyIds3,
        activeIngredientIds1: filters.activeIngredientIds1,
        activeIngredientIds2: filters.activeIngredientIds2,
        activeIngredientIds3: filters.activeIngredientIds3,
        atcHierarchyMatch: filters.atcHierarchyMatch,
        lastEncounterSubStartedFrom: filters.lastEncounterSubStartedFrom,
        lastEncounterSubStartedTo: filters.lastEncounterSubStartedTo,
        encounterStartedFrom: filters.encounterStartedFrom,
        encounterStartedTo: filters.encounterStartedTo,
        riskScoreFrom: filters.riskScoreFrom,
        riskScoreTo: filters.riskScoreTo,
        riskSeverities: filters.riskSeverities,
        evaluationStates: filters.evaluationStates,
        evaluationStateModifiedFrom: filters.evaluationStateModifiedFrom,
        evaluationStateModifiedTo: filters.evaluationStateModifiedTo,
        riskFactorTypes: filters.riskFactorTypes,
        riskFactorsCountFrom: filters.riskFactorsCountFrom,
        riskFactorsCountTo: filters.riskFactorsCountTo,
        hospitalizationStates: filters.hospitalizationStates,
        pharmaRecommendationRiskinesses: filters.pharmaRecommendationRiskinesses,
        pharmaRecommendationDateFrom: filters.pharmaRecommendationDateFrom,
        pharmaRecommendationDateTo: filters.pharmaRecommendationDateTo,
        pharmaRecommendationFollowUpDateFrom: filters.pharmaRecommendationFollowUpDateFrom,
        pharmaRecommendationFollowUpDateTo: filters.pharmaRecommendationFollowUpDateTo,
        commentSearchString: filters.commentSearchString,
        language: currentLocale.toUpperCase(),
        atcHierarchyFrom: filters.atcHierarchyFrom,
        atcHierarchyTo: filters.atcHierarchyTo,
        // Same source as the list query: unfinished rows are dropped and values normalized, so the exported
        // file can never be filtered differently from the table it was exported from.
        ...biochemistryParams(filters),
        biochemistryFrom: filters.biochemistryFrom,
        biochemistryTo: filters.biochemistryTo,
        biochemistryMatch: filters.biochemistryMatch,
        ...microbiologyParams(filters),
        microbiologyFrom: filters.microbiologyFrom,
        microbiologyTo: filters.microbiologyTo,
        microbiologyMatch: filters.microbiologyMatch,
        ...otherParams(filters),
      },
      locale: currentLocale,
    }).catch(() => {
      snackBar.enqueueSnackbar(t('common.export.failed'), {variant: 'error'});
    });
  }, [config.service.url.hospitalizationsExport, currentLocale, filters, snackBar, t]);

  const [confirmExportWarningOpened, setConfirmExportWarningOpened] = React.useState(false);
  const onConfirmExportWarning = useCallback(() => {
    setConfirmExportWarningOpened(false);
    downloadXlsxExport();
  }, [downloadXlsxExport]);

  const isWarningDialogVisible = useCallback(() => {
    if (!maxExportedRows) return false;
    return totalCount != null && totalCount > maxExportedRows;
  }, [totalCount, maxExportedRows]);

  const handleXlsxExport = useCallback(() => {
    if (isWarningDialogVisible()) {
      setConfirmExportWarningOpened(true);
    } else {
      downloadXlsxExport();
    }
  }, [isWarningDialogVisible, downloadXlsxExport]);

  const handleCancel = useCallback(() => {
    // noop
  }, []);

  const warningDialog = isWarningDialogVisible() ? (
    <ConfirmDialog
      isOpened={confirmExportWarningOpened}
      handleConfirm={onConfirmExportWarning}
      handleCancel={handleCancel}
      showCancel={false}
      titleIcon={
        <DlMuiDynamicIcon
          iconName='haidiWarningIcon'
          useFixedViewBox='0 0 13 12'
          css={muiStyles.confirmIconButton}
          svgIconInputCss={muiStyles.confirmIcon}
          noButtonStyles
        />
      }
      titleText={t('common.export.confirmDialogLimit.title')}
      confirmText={t('common.confirmDialog.accept')}
    >
      <Typography variant='value'>{t('common.export.confirmDialogLimit.text')}</Typography>
    </ConfirmDialog>
  ) : undefined;

  return {handleXlsxExport, warningDialog};
}

const getStyles: GetStylesFunction = (options) => ({
  confirmIconButton: css`
    color: var(--dlTheme-500);
    padding: 0;
  `,
  confirmIcon: css`
    font-size: 20px;
  `,
});
